import React, { useState } from 'react';

import { HeightOutlined } from '@mui/icons-material';
import { DividerProps, DividerPropsDefaults, DividerPropsSchema } from '@usewaypoint/block-divider';
import { Palette } from 'lucide-react';

import BaseSidebarPanel, { SectionHeader } from './helpers/BaseSidebarPanel';
import ColorInput from './helpers/inputs/ColorInput';
import SliderInput from './helpers/inputs/SliderInput';
import MultiStylePropertyPanel from './helpers/style-inputs/MultiStylePropertyPanel';
import { Separator } from '@/components/ui/separator';

type DividerSidebarPanelProps = {
  data: DividerProps;
  setData: (v: DividerProps) => void;
};

export default function DividerSidebarPanel({ data, setData }: DividerSidebarPanelProps) {
  const [, setErrors] = useState<Zod.ZodError | null>(null);

  const updateData = (d: unknown) => {
    const res = DividerPropsSchema.safeParse(d);
    if (res.success) {
      setData(res.data);
      setErrors(null);
    } else {
      setErrors(res.error);
    }
  };

  const lineColor = data.props?.lineColor ?? DividerPropsDefaults.lineColor;
  const lineHeight = data.props?.lineHeight ?? DividerPropsDefaults.lineHeight;

  return (
    <BaseSidebarPanel title="Divider block">
      {/* APPEARANCE SECTION */}
      <div className="space-y-4">
        <SectionHeader icon={Palette}>Appearance</SectionHeader>
        <div className="space-y-3">
          <ColorInput
            label="Color"
            defaultValue={lineColor}
            onChange={(lineColor) => updateData({ ...data, props: { ...data.props, lineColor } })}
          />
          <SliderInput
            label="Height"
            iconLabel={<HeightOutlined />}
            units="px"
            step={1}
            min={1}
            max={24}
            defaultValue={lineHeight}
            onChange={(lineHeight) => updateData({ ...data, props: { ...data.props, lineHeight } })}
          />
        </div>
      </div>

      <Separator className="my-6" />

      {/* LAYOUT SECTION */}
      <div className="space-y-4">
        <SectionHeader>Layout</SectionHeader>
        <div className="space-y-3">
          <MultiStylePropertyPanel
            names={['backgroundColor', 'padding']}
            value={data.style}
            onChange={(style) => updateData({ ...data, style })}
          />
        </div>
      </div>
    </BaseSidebarPanel>
  );
}
